import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Container,
  Box,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as VisibilityIcon,
  Mouse as ClickIcon,
  Share as ShareIcon,
  Comment as CommentIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  AttachMoney as AttachMoneyIcon,
  Link as LinkIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  SentimentNeutral as SentimentNeutralIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getPressReleases, PressReleaseResponse } from '../../services/prApi';
import { getIntegrations, getIntegrationMetrics, syncIntegration } from '../../services/integrationApi';
import { getCampaignStats } from '../../services/campaignApi';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Phase 5: Analytics Dashboard Components

// Color Palette with Dark Mode Support (matching Dashboard.tsx)
const getColors = (isDarkMode: boolean) => ({
  primary: '#0927eb',
  secondary: '#ff5200', // Orange (reduced usage)
  tertiary: isDarkMode ? '#555555' : '#cdcdcd', // Gray/Black
  black: '#000000', // Black for cards
  success: '#4caf50',
  error: '#f44336',
  white: '#ffffff',
  dark: isDarkMode ? '#f9fafb' : '#000000',
  gray: isDarkMode ? '#9ca3af' : '#666',
  background: isDarkMode ? '#0f1419' : '#ffffff',
  cardBg: isDarkMode ? '#1a1f29' : 'rgba(255, 255, 255, 0.95)',
  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
});

// Frosted Glass Styling
const getFrostedGlassStyle = (isDarkMode: boolean) => ({
  background: isDarkMode ? '#1a1f29' : 'rgba(255, 255, 255, 0.95)',
  borderRadius: '12px',
  boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.7)' : '0 4px 16px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)'
  }
});

// Frosted Glass Styling
const frostedGlassStyle = {
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)'
  }
};

// Interface for API data
interface PublishedArticle {
  distributionId: string;
  prId: string;
  publishedURL: string;
  publishedAt: string;
  channelName: string;
  tier: number;
  metrics: {
    impressions: number;
    views: number;
    clicks: number;
    sessions: number;
    shares: number;
    backlinks: number;
    ctr: number;
    engagementScore: number;
    lastUpdated: string;
  };
  crawlStatus: {
    isLive: boolean;
    lastCrawled: string;
    httpStatus: number;
  } | null;
}

interface DashboardMetrics {
  overview: {
    totalPRs: number;
    published: number;
    activeCampaigns: number;
    pendingPublications: number;
    publishedArticlesCount?: number;
  };
  performance: {
    totalImpressions: number;
    totalClicks: number;
    totalShares: number;
    totalBacklinks?: number;
    totalSessions?: number;
    totalConversions?: number;
    averageCTR: number;
    averageTimeOnPage: number;
    bounceRate: number;
  };
  business: {
    websiteTrafficIncrease: number;
    earnedBacklinks: number;
    conversionRate: number;
    leadGeneration: number;
    salesAttribution: number;
  };
  media: {
    earnedMediaValue: number;
    shareOfVoice: number;
    sentimentScore: number;
    positiveCoverage: number;
    neutralCoverage: number;
    negativeCoverage: number;
  };
  publishedArticles?: PublishedArticle[];
}

interface PaymentData {
  _id: string;
  amount: number;
  status: string;
  channels: Array<{
    channelId: string;
    channelName: string;
    status: string;
    price: number;
    publishedUrl?: string;
    publishedAt?: Date;
  }>;
  createdAt: Date;
  paidAt?: Date;
}



// Core Metrics Display Component
interface CoreMetricsDisplayProps {
  metrics: DashboardMetrics;
  loading: boolean;
  onRefresh: () => void;
  campaignStats?: {
    totalCampaigns: number;
    activeCampaigns: number;
    totalSpent: number;
    totalBudget: number;
    totalImpressions: number;
  } | null;
}

export const CoreMetricsDisplay: React.FC<CoreMetricsDisplayProps> = ({ metrics, loading, onRefresh, campaignStats }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const COLORS = getColors(isDarkMode);
  const frostedGlassStyle = getFrostedGlassStyle(isDarkMode);
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  
  // Reset to page 1 when articles change
  useEffect(() => {
    setCurrentPage(1);
  }, [metrics.publishedArticles?.length]);

  const getTrendIcon = (value: number, isPositive: boolean = true) => {
    if (isPositive) {
      return value > 0 ? <TrendingUpIcon color="success" /> : <TrendingDownIcon color="error" />;
    }
    return value > 0 ? <TrendingDownIcon color="error" /> : <TrendingUpIcon color="success" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} style={{ color: COLORS.primary }} />
      </Box>
    );
  }

  return (
    <div>
      {/* Overview Panel */}
      <Card style={{ ...frostedGlassStyle, marginBottom: "24px" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="16px">
            <Typography variant="h6" style={{ color: COLORS.dark, fontWeight: 600 }}>
              Overview Panel
            </Typography>
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Paper style={{ 
                padding: '16px', 
                textAlign: 'center',
                background: COLORS.primary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(9, 39, 235, 0.3)'
              }}>
                <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                  {metrics.overview.totalPRs}
                </Typography>
                <Typography variant="body2" style={{ color: 'white' }}>
                  Total Press Releases
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper style={{ 
                padding: '16px', 
                textAlign: 'center',
                background: COLORS.black,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(255, 82, 0, 0.3)'
              }}>
                <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                  {metrics.overview.published}
                </Typography>
                <Typography variant="body2" style={{ color: 'white' }}>
                  Published
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper style={{ 
                padding: '16px', 
                textAlign: 'center',
                background: COLORS.primary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(9, 39, 235, 0.3)'
              }}>
                <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                  {metrics.overview.activeCampaigns}
                </Typography>
                <Typography variant="body2" style={{ color: 'white' }}>
                  Active Campaigns
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper style={{ 
                padding: '16px', 
                textAlign: 'center',
                background: COLORS.secondary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(255, 82, 0, 0.3)'
              }}>
                <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                  {metrics.overview.pendingPublications}
                </Typography>
                <Typography variant="body2" style={{ color: 'white' }}>
                  Pending Publications
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>


      {/* Performance Metrics */}
      <Card style={{ ...frostedGlassStyle, marginBottom: "24px" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
            Performance Metrics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper style={{ 
                padding: "20px",
                background: COLORS.primary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(9, 39, 235, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <VisibilityIcon style={{ color: 'white', marginRight: '8px', fontSize: '28px' }} />
                  <Typography variant="subtitle1" style={{ color: 'white', fontWeight: 600 }}>Reach & Visibility</Typography>
                </div>
                <Typography variant="h3" style={{ color: 'white', fontWeight: 'bold' }}>
                  {(metrics.performance.totalImpressions / 1000).toFixed(1)}K
                </Typography>
                <Typography variant="body2" style={{ color: 'white', marginTop: '8px' }}>
                  Total Impressions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper style={{ 
                padding: "20px",
                background: COLORS.tertiary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                color: COLORS.dark
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <ClickIcon style={{ color: COLORS.dark, marginRight: '8px', fontSize: '28px' }} />
                  <Typography variant="subtitle1" style={{ color: COLORS.dark, fontWeight: 600 }}>Engagement Metrics</Typography>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="h5" style={{ color: COLORS.dark, fontWeight: 'bold' }}>{metrics.performance.totalClicks.toLocaleString()}</Typography>
                    <Typography variant="body2" style={{ color: COLORS.gray }}>Total Clicks</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h5" style={{ color: COLORS.dark, fontWeight: 'bold' }}>{metrics.performance.averageCTR.toFixed(2)}%</Typography>
                    <Typography variant="body2" style={{ color: COLORS.gray }}>CTR</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h5" style={{ color: COLORS.dark, fontWeight: 'bold' }}>{metrics.performance.totalShares.toLocaleString()}</Typography>
                    <Typography variant="body2" style={{ color: COLORS.gray }}>Shares</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h5" style={{ color: COLORS.dark, fontWeight: 'bold' }}>
                      {metrics.performance.averageTimeOnPage ? 
                        `${Math.round(metrics.performance.averageTimeOnPage)}s` : 
                        '0s'}
                    </Typography>
                    <Typography variant="body2" style={{ color: COLORS.gray }}>Avg. Time</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Business Impact */}
      <Card style={{ ...frostedGlassStyle, marginBottom: "24px" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
            Business Impact
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper style={{ 
                padding: "20px",
                background: COLORS.tertiary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                color: COLORS.dark
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <PublicIcon style={{ color: COLORS.dark, marginRight: '8px', fontSize: '28px' }} />
                  <Typography variant="subtitle1" style={{ color: COLORS.dark, fontWeight: 600 }}>Website Traffic</Typography>
                </div>
                <Typography variant="h4" style={{ color: COLORS.dark, fontWeight: 'bold' }}>
                  {(() => {
                    // Calculate as 5% of total impressions, but show 0 if no impressions
                    const calculatedSessions = metrics.performance.totalImpressions > 0 
                      ? Math.round(metrics.performance.totalImpressions * 0.05) 
                      : 0;
                    return calculatedSessions.toLocaleString();
                  })()}
                </Typography>
                <Typography variant="body2" style={{ color: COLORS.gray }}>
                  Total Sessions
                </Typography>
                
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper style={{ 
                padding: "20px",
                background: COLORS.primary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(9, 39, 235, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <LinkIcon style={{ color: 'white', marginRight: '8px', fontSize: '28px' }} />
                  <Typography variant="subtitle1" style={{ color: 'white', fontWeight: 600 }}>Earned Backlinks</Typography>
                </div>
                <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                  0
                </Typography>
                <Typography variant="body2" style={{ color: 'white' }}>
                  Total Backlinks
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper style={{ 
                padding: "20px",
                background: COLORS.secondary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                color: COLORS.dark
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <AttachMoneyIcon style={{ color: COLORS.white, marginRight: '8px', fontSize: '28px' }} />
                  <Typography variant="subtitle1" style={{ color: COLORS.white, fontWeight: 600 }}>Conversions</Typography>
                </div>
                <Typography variant="h4" style={{ color: COLORS.white, fontWeight: 'bold' }}>
                  {(() => {
                    // Calculate as 5% of total impressions, but show 0 if no impressions
                    const calculatedConversions = metrics.performance.totalImpressions > 0 
                      ? Math.round(metrics.performance.totalImpressions * 0.02) 
                      : 0;
                    return calculatedConversions.toLocaleString();
                  })()}
                </Typography>
                <Typography variant="body2" style={{ color: COLORS.white }}>
                  Total Conversions
                </Typography>
                
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Media Value */}
      <Card style={{ ...frostedGlassStyle, marginBottom: "24px" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
            Media Value & Sentiment
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper style={{ 
                padding: "20px",
                background: COLORS.primary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(9, 39, 235, 0.3)'
              }}>
                <Typography variant="subtitle1" gutterBottom style={{ color: 'white', fontWeight: 600 }}>
                  Earned Media Value (EMV)
                </Typography>
                <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                  ${(metrics.media.earnedMediaValue / 1000).toFixed(0)}K
                </Typography>
                <Typography variant="body2" style={{ color: 'white', marginTop: '8px' }}>
                  Calculated vs. ad spend equivalent
                </Typography>
                <div style={{ marginTop: "16px" }}>
                  <Typography variant="body2" style={{ color: 'white' }}>Share of Voice: {metrics.media.shareOfVoice.toFixed(1)}%</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={metrics.media.shareOfVoice} 
                    style={{ 
                      marginTop: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.3)'
                    }}
                  />
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper style={{ 
                padding: "20px",
                background: COLORS.tertiary,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                color: COLORS.dark
              }}>
                <Typography variant="subtitle1" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
                  Sentiment Analysis
                </Typography>
                <Typography variant="h4" style={{ color: COLORS.dark, fontWeight: 'bold' }}>
                  {metrics.media.sentimentScore}%
                </Typography>
                <Typography variant="body2" style={{ color: COLORS.gray, marginTop: '8px' }}>
                  Overall Sentiment Score
                </Typography>
                <div style={{ marginTop: "16px" }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <SentimentSatisfiedIcon style={{ color: COLORS.success, marginRight: '8px' }} />
                    <Typography variant="body2" style={{ color: COLORS.dark }}>Positive: {metrics.media.positiveCoverage}%</Typography>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <SentimentNeutralIcon style={{ color: COLORS.gray, marginRight: '8px' }} />
                    <Typography variant="body2" style={{ color: COLORS.dark }}>Neutral: {metrics.media.neutralCoverage}%</Typography>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <SentimentDissatisfiedIcon style={{ color: COLORS.error, marginRight: '8px' }} />
                    <Typography variant="body2" style={{ color: COLORS.dark }}>Negative: {metrics.media.negativeCoverage}%</Typography>
                  </div>
                </div>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Published Articles Section */}
      {metrics.publishedArticles && metrics.publishedArticles.length > 0 && (() => {
        const articlesPerPage = 5;
        const sortedArticles = [...metrics.publishedArticles].sort((a, b) => {
          const dateA = new Date(a.publishedAt || 0).getTime();
          const dateB = new Date(b.publishedAt || 0).getTime();
          return dateB - dateA; // Latest first
        });
        const totalPages = Math.ceil(sortedArticles.length / articlesPerPage);
        const startIndex = (currentPage - 1) * articlesPerPage;
        const endIndex = startIndex + articlesPerPage;
        const currentArticles = sortedArticles.slice(startIndex, endIndex);
        
        return (
        <Card style={{ ...frostedGlassStyle, marginBottom: "24px" }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="24px">
              <Typography variant="h6" style={{ color: COLORS.dark, fontWeight: 600 }}>
                Published Articles Analytics
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" style={{ color: COLORS.gray }}>
                  Showing {startIndex + 1}-{Math.min(endIndex, sortedArticles.length)} of {sortedArticles.length} articles
                </Typography>
                {totalPages > 1 && (
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      style={{
                        borderColor: COLORS.primary,
                        color: COLORS.primary,
                        minWidth: '80px'
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      style={{
                        borderColor: COLORS.primary,
                        color: COLORS.primary,
                        minWidth: '80px'
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>Publication</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>URL</TableCell>
                    <TableCell style={{ fontWeight: 600 }} align="right">Impressions</TableCell>
                    <TableCell style={{ fontWeight: 600 }} align="right">Clicks</TableCell>
                    <TableCell style={{ fontWeight: 600 }} align="right">Shares</TableCell>
                    <TableCell style={{ fontWeight: 600 }} align="right">CTR</TableCell>
                    <TableCell style={{ fontWeight: 600 }} align="right">Backlinks</TableCell>
                    <TableCell style={{ fontWeight: 600 }} align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentArticles.map((article, index) => {
                    // Extract publication name from URL if channelName is 'Unknown'
                    const getPublicationName = () => {
                      if (article.channelName && article.channelName !== 'Unknown') {
                        return article.channelName;
                      }
                      // Try to extract domain name from URL
                      try {
                        if (article.publishedURL) {
                          const url = new URL(article.publishedURL);
                          const hostname = url.hostname.replace('www.', '');
                          // Capitalize first letter of each word
                          return hostname.split('.').slice(0, -1).map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ') || hostname;
                        }
                      } catch (e) {
                        // If URL parsing fails, return Unknown
                      }
                      return 'Unknown Publication';
                    };

                    const publicationName = getPublicationName();
                    const tier = article.tier || 3; // Default to tier 3 if not available

                    return (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {publicationName}
                        </Typography>
                        <Typography variant="caption" style={{ color: COLORS.gray }}>
                          Tier {tier}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <a 
                          href={article.publishedURL} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: COLORS.primary, textDecoration: 'none' }}
                        >
                          <Typography variant="body2" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {article.publishedURL}
                          </Typography>
                        </a>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {article.metrics.impressions.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" style={{ fontWeight: 500, color: COLORS.primary }}>
                          {article.metrics.clicks.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {article.metrics.shares.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {article.metrics.ctr.toFixed(2)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {article.metrics.backlinks}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          {article.crawlStatus ? (
                            <Chip
                              label={article.crawlStatus.isLive ? 'Live' : 'Offline'}
                              color={article.crawlStatus.isLive ? 'success' : 'error'}
                              size="small"
                            />
                          ) : (
                            <Chip label="Pending" color="default" size="small" />
                          )}
                          {(article.metrics.impressions === 0 || article.metrics.backlinks === 0) && (
                            <Tooltip title="Trigger crawl to update metrics">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await fetch(`${API_BASE_URL}/api/crawl/${article.distributionId}`, {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      }
                                    });
                                    if (response.ok) {
                                      setSnackbarMessage('Crawl job queued. Metrics will update shortly.');
                                      setSnackbarSeverity('success');
                                      setSnackbarOpen(true);
                                      // Refresh data after 5 seconds
                                      setTimeout(() => {
                                        onRefresh();
                                      }, 5000);
                                    } else {
                                      throw new Error('Failed to trigger crawl');
                                    }
                                  } catch (error) {
                                    setSnackbarMessage('Failed to trigger crawl. Please try again.');
                                    setSnackbarSeverity('error');
                                    setSnackbarOpen(true);
                                  }
                                }}
                                style={{ padding: '4px' }}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" alignItems="center" gap={2} marginTop="16px">
                <Typography variant="body2" style={{ color: COLORS.gray }}>
                  Page {currentPage} of {totalPages}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        );
      })()}

      {/* Charts Section */}
      <Card style={{ ...frostedGlassStyle, marginBottom: "24px" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: COLORS.dark, fontWeight: 600, marginBottom: '24px' }}>
            Performance Charts
          </Typography>
          
          <Grid container spacing={3}>
            {/* Performance Metrics Bar Chart */}
            <Grid item xs={12} md={6}>
              <Paper style={{ padding: '16px', ...frostedGlassStyle }}>
                <Typography variant="subtitle1" style={{ color: COLORS.dark, fontWeight: 600, marginBottom: '16px' }}>
                  Key Performance Metrics
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={[
                    { metric: 'Impressions', value: metrics.performance.totalImpressions || 0 },
                    { metric: 'Clicks', value: metrics.performance.totalClicks || 0 },
                    { metric: 'Shares', value: metrics.performance.totalShares || 0 },
                    { metric: 'Backlinks', value: metrics.performance.totalBacklinks || 0 },
                    { metric: 'Sessions', value: metrics.performance.totalSessions || 0 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="metric" stroke="#666" />
                    <YAxis stroke="#666" />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="value" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Sentiment Pie Chart */}
            <Grid item xs={12} md={6}>
              <Paper style={{ padding: '16px', ...frostedGlassStyle }}>
                <Typography variant="subtitle1" style={{ color: COLORS.dark, fontWeight: 600, marginBottom: '16px' }}>
                  Sentiment Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Positive', value: metrics.media.positiveCoverage, color: COLORS.success },
                        { name: 'Neutral', value: metrics.media.neutralCoverage, color: COLORS.tertiary },
                        { name: 'Negative', value: metrics.media.negativeCoverage, color: COLORS.error }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label
                    >
                      {[
                        { name: 'Positive', value: metrics.media.positiveCoverage, color: COLORS.success },
                        { name: 'Neutral', value: metrics.media.neutralCoverage, color: COLORS.tertiary },
                        { name: 'Negative', value: metrics.media.negativeCoverage, color: COLORS.error }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px'
                      }} 
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

// Temporal Analysis Component
export const TemporalAnalysis: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const COLORS = getColors(isDarkMode);
  const frostedGlassStyle = getFrostedGlassStyle(isDarkMode);
  
  const [timeRange, setTimeRange] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [temporalData, setTemporalData] = useState<any[]>([]);

  useEffect(() => {
    fetchTemporalData();
  }, [timeRange]);

  const fetchTemporalData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch dashboard data which includes published articles with metrics
      const response = await fetch(`${API_BASE_URL}/api/pr/analytics/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const dashboard = data.dashboard || data;
        const publishedArticles = dashboard.publishedArticles || [];
        
        // Generate time-based data from published articles
        const currentDate = new Date();
        let months: string[] = [];
        let dateRange: number = 6;
        
        if (timeRange === 'monthly') {
          months = [];
          dateRange = 6;
          for (let i = dateRange - 1; i >= 0; i--) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
            months.push(date.toLocaleDateString('en-US', { month: 'short' }));
          }
        } else if (timeRange === 'quarterly') {
          months = [];
          dateRange = 4;
          for (let i = dateRange - 1; i >= 0; i--) {
            const quarter = Math.floor((currentDate.getMonth() - i * 3) / 3) + 1;
            const year = currentDate.getFullYear();
            months.push(`Q${quarter} ${year}`);
          }
        } else {
          months = [];
          dateRange = 2;
          for (let i = dateRange - 1; i >= 0; i--) {
            months.push(`${currentDate.getFullYear() - i}`);
          }
        }
        
        const temporalMetrics = months.map((monthLabel, index) => {
          const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - (dateRange - 1 - index), 1);
          const nextMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);
          
          const articlesInPeriod = publishedArticles.filter((article: any) => {
            if (!article.publishedAt) return false;
            const pubDate = new Date(article.publishedAt);
            return pubDate >= monthDate && pubDate < nextMonth;
          });

          const impressions = articlesInPeriod.reduce((sum: number, article: any) => 
            sum + (article.metrics?.impressions || 0), 0);
          const clicks = articlesInPeriod.reduce((sum: number, article: any) => 
            sum + (article.metrics?.clicks || 0), 0);
          const shares = articlesInPeriod.reduce((sum: number, article: any) => 
            sum + (article.metrics?.shares || 0), 0);
          const backlinks = articlesInPeriod.reduce((sum: number, article: any) => 
            sum + (article.metrics?.backlinks || 0), 0);

          return {
            month: monthLabel,
            prs: articlesInPeriod.length,
            published: articlesInPeriod.length,
            impressions,
            clicks,
            shares,
            backlinks
          };
        });
        
        setTemporalData(temporalMetrics);
      }
    } catch (error) {
      console.error('Error fetching temporal data:', error);
      setTemporalData([]);
    } finally {
      setLoading(false);
    }
  };

  const monthlyData = temporalData;

  return (
    <Card style={{ marginBottom: "24px", ...frostedGlassStyle }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Typography variant="h6" style={{ color: COLORS.dark, fontWeight: 600 }}>
            Temporal Analysis
          </Typography>
          <FormControl size="small" style={{ minWidth: '120px' }}>
            <InputLabel style={{ color: COLORS.gray }}>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{ color: COLORS.dark }}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
        </div>

        {loading ? (
          <Box display="flex" justifyContent="center" padding="40px">
            <CircularProgress style={{ color: COLORS.primary }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper style={{ padding: "16px", ...frostedGlassStyle }}>
                  <Typography variant="subtitle1" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
                  Performance Trends ({timeRange === 'monthly' ? 'Last 6 Months' : timeRange === 'quarterly' ? 'Last 4 Quarters' : 'Last 2 Years'})
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#444' : '#e0e0e0'} />
                    <XAxis dataKey="month" stroke={COLORS.gray} />
                    <YAxis stroke={COLORS.gray} />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: COLORS.cardBg, 
                        border: `1px solid ${COLORS.borderColor}`,
                        borderRadius: '8px',
                        color: COLORS.dark
                      }} 
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="impressions" 
                      stroke={COLORS.primary} 
                      strokeWidth={3}
                      name="Impressions"
                      dot={{ fill: COLORS.primary, strokeWidth: 2, r: 5 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="clicks" 
                      stroke={COLORS.secondary} 
                      strokeWidth={3}
                      name="Clicks"
                      dot={{ fill: COLORS.secondary, strokeWidth: 2, r: 5 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="shares" 
                      stroke={COLORS.success} 
                      strokeWidth={3}
                      name="Shares"
                      dot={{ fill: COLORS.success, strokeWidth: 2, r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper style={{ padding: "20px", ...frostedGlassStyle }}>
                <Typography variant="subtitle1" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
                  Insights & Predictions
                </Typography>
                <div style={{ marginBottom: "16px" }}>
                  <Typography variant="body2" style={{ color: COLORS.gray, marginBottom: '8px' }}>
                    <strong>Best Publishing Days</strong>
                  </Typography>
                  <Chip label="Tuesday" size="small" style={{ marginRight: '4px', marginBottom: '4px', backgroundColor: COLORS.primary, color: 'white' }} />
                  <Chip label="Wednesday" size="small" style={{ marginRight: '4px', marginBottom: '4px', backgroundColor: COLORS.secondary, color: 'white' }} />
                  <Chip label="Thursday" size="small" style={{ marginRight: '4px', marginBottom: '4px', backgroundColor: COLORS.success, color: 'white' }} />
                </div>
                <Divider style={{ margin: '16px 0', backgroundColor: COLORS.borderColor }} />
                <div style={{ marginBottom: "16px" }}>
                  <Typography variant="body2" style={{ color: COLORS.gray, marginBottom: '8px' }}>
                    <strong>Peak Engagement Time</strong>
                  </Typography>
                  <Typography variant="body2" style={{ color: COLORS.dark }}>10:00 AM - 2:00 PM EST</Typography>
                </div>
                <Divider style={{ margin: '16px 0', backgroundColor: COLORS.borderColor }} />
                <div>
                  <Typography variant="body2" style={{ color: COLORS.gray, marginBottom: '8px' }}>
                    <strong>Trend Analysis</strong>
                  </Typography>
                  <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', color: COLORS.dark }}>
                    <TrendingUpIcon style={{ color: COLORS.success, fontSize: '18px', marginRight: '4px' }} />
                    {monthlyData.length > 1 && monthlyData[0]?.impressions > 0 ? 
                      `+${((monthlyData[monthlyData.length - 1]?.impressions || 0) / (monthlyData[0]?.impressions || 1) * 100 - 100).toFixed(0)}% Growth` : 
                      'No growth data'}
                  </Typography>
                  <Typography variant="body2" style={{ color: COLORS.dark }}>
                    Total Articles: {monthlyData.reduce((sum: number, d: any) => sum + (d.prs || 0), 0)}
                  </Typography>
                </div>
              </Paper>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

// Competitive Intelligence Component
export const CompetitiveIntelligence: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const COLORS = getColors(isDarkMode);
  const frostedGlassStyle = getFrostedGlassStyle(isDarkMode);
  
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Fetch real dashboard data
      const response = await fetch(`${API_BASE_URL}/api/pr/analytics/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const dashboard = data.dashboard || data;
        const performance = dashboard.performance || {};
        const overview = dashboard.overview || {};
        
        // Calculate real user metrics
        const totalImpressions = performance.totalImpressions || 0;
        const totalClicks = performance.totalClicks || 0;
        const totalShares = performance.totalShares || 0;
        const totalBacklinks = performance.totalBacklinks || 0;
        const avgCTR = performance.averageCTR || 0;
        const publishedCount = overview.publishedPRs || overview.published || 0;
        const totalPRs = overview.totalPRs || 0;
        
        // Calculate average impressions per published article
        const avgImpressions = publishedCount > 0 ? Math.floor(totalImpressions / publishedCount) : 0;
        
        // Calculate PRs per month (estimate from total PRs / 6 months)
        const prsPerMonth = Math.floor(totalPRs / 6);

      setMetrics({
        userMetrics: {
          prCount: totalPRs,
          publishedCount,
          avgImpressions,
          ctr: avgCTR.toFixed(2),
          sentimentScore: 78 // Default until sentiment analysis is implemented
        },
        industryBenchmarks: {
          avgPRsPerMonth: 8,
          avgImpressions: 12000,
          avgCTR: 3.5,
          avgSentiment: 75
        }
      });
      } else {
        throw new Error('Failed to fetch dashboard data');
      }
    } catch (error: any) {
      console.error('Error fetching metrics:', error);
      setError(error.message);
      // Set default metrics to show something
      setMetrics({
        userMetrics: {
          prCount: 0,
          publishedCount: 0,
          avgImpressions: 0,
          ctr: '0.00',
          sentimentScore: 0
        },
        industryBenchmarks: {
          avgPRsPerMonth: 8,
          avgImpressions: 12000,
          avgCTR: 3.5,
          avgSentiment: 75
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card style={{ marginBottom: "24px", ...frostedGlassStyle }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
            Competitive Intelligence & Benchmarking
          </Typography>
          <Box display="flex" justifyContent="center" padding="40px">
            <CircularProgress style={{ color: COLORS.primary }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card style={{ marginBottom: "24px", ...frostedGlassStyle }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
            Competitive Intelligence & Benchmarking
          </Typography>
          <Alert severity="info">
            No data available yet. Create and publish press releases to see competitive insights.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const comparisonData = [
    { 
      metric: 'Avg Impressions', 
      you: metrics.userMetrics.avgImpressions, 
      industry: metrics.industryBenchmarks.avgImpressions,
      color: COLORS.primary
    },
    { 
      metric: 'CTR (%)', 
      you: parseFloat(metrics.userMetrics.ctr), 
      industry: metrics.industryBenchmarks.avgCTR,
      color: COLORS.secondary
    },
    { 
      metric: 'PRs/Month', 
      you: Math.max(0, Math.floor(metrics.userMetrics.prCount / 6)), // Approximate monthly average
      industry: metrics.industryBenchmarks.avgPRsPerMonth,
      color: COLORS.success
    }
  ];

  return (
    <Card style={{ marginBottom: "24px", ...frostedGlassStyle }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Typography variant="h6" style={{ color: COLORS.dark, fontWeight: 600 }}>
            Competitive Intelligence & Benchmarking
          </Typography>
          <IconButton onClick={fetchMetrics} size="small">
            <RefreshIcon style={{ color: COLORS.gray }} />
          </IconButton>
        </div>

        {error && (
          <Alert severity="warning" style={{ marginBottom: '16px' }}>
            {error} - Showing available data
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {/* Your Performance */}
          <Grid item xs={12} md={6}>
            <Paper style={{ 
              padding: "20px", 
              background: COLORS.primary,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(9, 39, 235, 0.3)'
            }}>
              <Typography variant="subtitle1" gutterBottom style={{ color: 'white', fontWeight: 600 }}>
                Your Performance
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.userMetrics.prCount}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>Total PRs</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.userMetrics.publishedCount}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>Published</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.userMetrics.avgImpressions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>Avg. Impressions</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.userMetrics.ctr}%
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>CTR</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Industry Benchmarks */}
          <Grid item xs={12} md={6}>
            <Paper style={{ 
              padding: "20px", 
              background: COLORS.secondary,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(255, 82, 0, 0.3)'
            }}>
              <Typography variant="subtitle1" gutterBottom style={{ color: 'white', fontWeight: 600 }}>
                Industry Benchmarks
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.industryBenchmarks.avgPRsPerMonth}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>PRs/Month</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.industryBenchmarks.avgImpressions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>Avg. Impressions</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.industryBenchmarks.avgCTR}%
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>Avg. CTR</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" style={{ color: 'white', fontWeight: 'bold' }}>
                    {metrics.industryBenchmarks.avgSentiment}%
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>Sentiment</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Comparison Chart */}
          <Grid item xs={12}>
            <Paper style={{ padding: "20px", ...frostedGlassStyle }}>
              <Typography variant="subtitle1" gutterBottom style={{ color: COLORS.dark, fontWeight: 600 }}>
                You vs Industry Average
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#444' : '#e0e0e0'} />
                  <XAxis dataKey="metric" stroke={COLORS.gray} />
                  <YAxis stroke={COLORS.gray} />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: COLORS.cardBg, 
                      border: `1px solid ${COLORS.borderColor}`,
                      borderRadius: '8px',
                      color: COLORS.dark
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="you" name="Your Performance" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                  <Bar dataKey="industry" name="Industry Average" fill={COLORS.tertiary} radius={[8, 8, 0, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Export & Reporting Component
export const ExportReporting: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const COLORS = getColors(isDarkMode);
  const frostedGlassStyle = getFrostedGlassStyle(isDarkMode);
  
  const [exportFormat, setExportFormat] = useState('json');
  const [dateRange, setDateRange] = useState('last30days');
  const [isExporting, setIsExporting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Convert date range string to actual date range
  const getDateRange = (range: string): { startDate: Date; endDate: Date } => {
    const endDate = new Date();
    let startDate = new Date();
    
    switch (range) {
      case 'last7days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last90days':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'lastyear':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case 'alltime':
        startDate = new Date(0); // Beginning of time
        break;
      default:
        startDate.setDate(endDate.getDate() - 30);
    }
    
    return { startDate, endDate };
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Fetch dashboard data to export
      const dashboardResponse = await fetch(`${API_BASE_URL}/api/pr/analytics/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!dashboardResponse.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const dashboardData = await dashboardResponse.json();
      const dashboard = dashboardData.dashboard || dashboardData;

      // Prepare export data
      const { startDate, endDate } = getDateRange(dateRange);
      const exportData = {
        reportInfo: {
          generatedAt: new Date().toISOString(),
          dateRange: {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            label: dateRange
          },
          format: exportFormat
        },
        overview: dashboard.overview || {},
        performance: dashboard.performance || {},
        publishedArticles: dashboard.publishedArticles || [],
        recentActivity: dashboard.recentActivity || []
      };

      // Generate file based on format
      let blob: Blob | null = null;
      let filename: string = '';
      let mimeType: string = '';

      if (exportFormat === 'csv') {
        // Generate CSV
        const csvRows: string[] = [];
        
        // Header
        csvRows.push('Metric,Value');
        
        // Overview metrics
        csvRows.push('\nOverview Metrics');
        if (exportData.overview.totalPRs !== undefined) csvRows.push(`Total PRs,${exportData.overview.totalPRs}`);
        if (exportData.overview.publishedPRs !== undefined) csvRows.push(`Published PRs,${exportData.overview.publishedPRs}`);
        if (exportData.overview.publishedArticlesCount !== undefined) csvRows.push(`Published Articles,${exportData.overview.publishedArticlesCount}`);
        
        // Performance metrics
        csvRows.push('\nPerformance Metrics');
        if (exportData.performance.totalImpressions !== undefined) csvRows.push(`Total Impressions,${exportData.performance.totalImpressions}`);
        if (exportData.performance.totalClicks !== undefined) csvRows.push(`Total Clicks,${exportData.performance.totalClicks}`);
        if (exportData.performance.totalShares !== undefined) csvRows.push(`Total Shares,${exportData.performance.totalShares}`);
        if (exportData.performance.averageCTR !== undefined) csvRows.push(`Average CTR,${exportData.performance.averageCTR}%`);
        
        // Published articles
        if (exportData.publishedArticles.length > 0) {
          csvRows.push('\nPublished Articles');
          csvRows.push('Publication,URL,Tier,Impressions,Clicks,Shares,CTR,Backlinks');
          exportData.publishedArticles.forEach((article: any) => {
            const pubName = article.channelName || 'Unknown';
            const url = article.publishedURL || '';
            const tier = article.tier || 'N/A';
            const impressions = article.metrics?.impressions || 0;
            const clicks = article.metrics?.clicks || 0;
            const shares = article.metrics?.shares || 0;
            const ctr = article.metrics?.ctr || 0;
            const backlinks = article.metrics?.backlinks || 0;
            csvRows.push(`"${pubName}","${url}",${tier},${impressions},${clicks},${shares},${ctr},${backlinks}`);
          });
        }
        
        const csvContent = csvRows.join('\n');
        blob = new Blob([csvContent], { type: 'text/csv' });
        filename = `analytics-report-${Date.now()}.csv`;
        mimeType = 'text/csv';
      } else if (exportFormat === 'json') {
        // Generate JSON
        const jsonContent = JSON.stringify(exportData, null, 2);
        blob = new Blob([jsonContent], { type: 'application/json' });
        filename = `analytics-report-${Date.now()}.json`;
        mimeType = 'application/json';
      } else if (exportFormat === 'pdf') {
        // Generate PDF
        const doc = new jsPDF();
        let yPos = 20;
        
        // Title
        doc.setFontSize(18);
        doc.setTextColor(9, 39, 235); // Primary color
        doc.text('Analytics Report', 14, yPos);
        yPos += 10;
        
        // Report Info
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(`Generated: ${new Date(exportData.reportInfo.generatedAt).toLocaleString()}`, 14, yPos);
        yPos += 6;
        doc.text(`Date Range: ${exportData.reportInfo.dateRange.label}`, 14, yPos);
        yPos += 6;
        doc.text(`${new Date(exportData.reportInfo.dateRange.start).toLocaleDateString()} - ${new Date(exportData.reportInfo.dateRange.end).toLocaleDateString()}`, 14, yPos);
        yPos += 15;
        
        // Overview Metrics
        doc.setFontSize(14);
        doc.setTextColor(9, 39, 235);
        doc.text('Overview Metrics', 14, yPos);
        yPos += 8;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        if (exportData.overview.totalPRs !== undefined) {
          doc.text(`Total PRs: ${exportData.overview.totalPRs}`, 20, yPos);
          yPos += 6;
        }
        if (exportData.overview.publishedPRs !== undefined) {
          doc.text(`Published PRs: ${exportData.overview.publishedPRs}`, 20, yPos);
          yPos += 6;
        }
        if (exportData.overview.publishedArticlesCount !== undefined) {
          doc.text(`Published Articles: ${exportData.overview.publishedArticlesCount}`, 20, yPos);
          yPos += 6;
        }
        yPos += 5;
        
        // Performance Metrics
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(14);
        doc.setTextColor(9, 39, 235);
        doc.text('Performance Metrics', 14, yPos);
        yPos += 8;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        if (exportData.performance.totalImpressions !== undefined) {
          doc.text(`Total Impressions: ${exportData.performance.totalImpressions.toLocaleString()}`, 20, yPos);
          yPos += 6;
        }
        if (exportData.performance.totalClicks !== undefined) {
          doc.text(`Total Clicks: ${exportData.performance.totalClicks.toLocaleString()}`, 20, yPos);
          yPos += 6;
        }
        if (exportData.performance.totalShares !== undefined) {
          doc.text(`Total Shares: ${exportData.performance.totalShares.toLocaleString()}`, 20, yPos);
          yPos += 6;
        }
        if (exportData.performance.averageCTR !== undefined) {
          doc.text(`Average CTR: ${exportData.performance.averageCTR}%`, 20, yPos);
          yPos += 6;
        }
        if (exportData.performance.totalBacklinks !== undefined) {
          doc.text(`Total Backlinks: ${exportData.performance.totalBacklinks.toLocaleString()}`, 20, yPos);
          yPos += 6;
        }
        yPos += 5;
        
        // Published Articles
        if (exportData.publishedArticles.length > 0) {
          if (yPos > 230) {
            doc.addPage();
            yPos = 20;
          }
          doc.setFontSize(14);
          doc.setTextColor(9, 39, 235);
          doc.text('Published Articles', 14, yPos);
          yPos += 8;
          doc.setFontSize(9);
          doc.setTextColor(0, 0, 0);
          
          exportData.publishedArticles.forEach((article: any, index: number) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            const pubName = article.channelName || 'Unknown';
            doc.setFontSize(10);
            doc.text(`${index + 1}. ${pubName} (Tier ${article.tier || 'N/A'})`, 20, yPos);
            yPos += 6;
            doc.setFontSize(9);
            doc.text(`   URL: ${article.publishedURL || 'N/A'}`, 20, yPos);
            yPos += 5;
            doc.text(`   Impressions: ${(article.metrics?.impressions || 0).toLocaleString()}`, 20, yPos);
            yPos += 5;
            doc.text(`   Clicks: ${(article.metrics?.clicks || 0).toLocaleString()}`, 20, yPos);
            yPos += 5;
            doc.text(`   Shares: ${(article.metrics?.shares || 0).toLocaleString()}`, 20, yPos);
            yPos += 5;
            doc.text(`   CTR: ${(article.metrics?.ctr || 0).toFixed(2)}%`, 20, yPos);
            yPos += 5;
            doc.text(`   Backlinks: ${(article.metrics?.backlinks || 0).toLocaleString()}`, 20, yPos);
            yPos += 8;
          });
        }
        
        const pdfBlob = doc.output('blob');
        blob = pdfBlob;
        filename = `analytics-report-${Date.now()}.pdf`;
        mimeType = 'application/pdf';
      } else if (exportFormat === 'excel') {
        // Generate Excel
        const workbook = XLSX.utils.book_new();
        
        // Overview Sheet
        const overviewData = [
          ['Overview Metrics'],
          ['Metric', 'Value'],
          ['Total PRs', exportData.overview.totalPRs || 0],
          ['Published PRs', exportData.overview.publishedPRs || 0],
          ['Published Articles', exportData.overview.publishedArticlesCount || 0],
          ['Active Campaigns', exportData.overview.activeCampaigns || 0],
          ['Pending Publications', exportData.overview.pendingPublications || 0]
        ];
        const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
        XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');
        
        // Performance Sheet
        const performanceData = [
          ['Performance Metrics'],
          ['Metric', 'Value'],
          ['Total Impressions', exportData.performance.totalImpressions || 0],
          ['Total Clicks', exportData.performance.totalClicks || 0],
          ['Total Shares', exportData.performance.totalShares || 0],
          ['Average CTR', `${exportData.performance.averageCTR || 0}%`],
          ['Total Backlinks', exportData.performance.totalBacklinks || 0],
          ['Total Sessions', exportData.performance.totalSessions || 0],
          ['Total Conversions', exportData.performance.totalConversions || 0]
        ];
        const performanceSheet = XLSX.utils.aoa_to_sheet(performanceData);
        XLSX.utils.book_append_sheet(workbook, performanceSheet, 'Performance');
        
        // Published Articles Sheet
        if (exportData.publishedArticles.length > 0) {
          const articlesData = [
            ['Published Articles'],
            ['Publication', 'URL', 'Tier', 'Impressions', 'Clicks', 'Shares', 'CTR', 'Backlinks', 'Sessions']
          ];
          exportData.publishedArticles.forEach((article: any) => {
            articlesData.push([
              article.channelName || 'Unknown',
              article.publishedURL || '',
              article.tier || 'N/A',
              article.metrics?.impressions || 0,
              article.metrics?.clicks || 0,
              article.metrics?.shares || 0,
              article.metrics?.ctr || 0,
              article.metrics?.backlinks || 0,
              article.metrics?.sessions || 0
            ]);
          });
          const articlesSheet = XLSX.utils.aoa_to_sheet(articlesData);
          XLSX.utils.book_append_sheet(workbook, articlesSheet, 'Published Articles');
        }
        
        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        filename = `analytics-report-${Date.now()}.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }

      if (!blob || !filename) {
        throw new Error('Failed to generate export file');
      }

      // Download the file
      const url = window.URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSnackbarMessage('Report exported successfully!');
      setSnackbarSeverity('success');
    } catch (error: any) {
      console.error('Export error:', error);
      setSnackbarMessage(error.message || 'Failed to export report. Please try again.');
      setSnackbarSeverity('error');
    } finally {
      setIsExporting(false);
      setSnackbarOpen(true);
    }
  };

  return (
    <Card style={{ marginBottom: "24px", ...frostedGlassStyle }}>
      <CardContent>
        <Typography variant="h6" gutterBottom style={{ color: COLORS.dark, fontWeight: 600, marginBottom: '24px' }}>
          Export & Reporting
        </Typography>
        
        <Grid container spacing={3}>
          {/* Export Options */}
          <Grid item xs={12} md={6}>
            <Paper style={{ padding: "24px", ...frostedGlassStyle }}>
              <Typography variant="subtitle1" gutterBottom style={{ color: COLORS.dark, fontWeight: 600, marginBottom: '20px' }}>
                Export Options
              </Typography>
              
              <FormControl fullWidth style={{ marginBottom: "20px" }}>
                <InputLabel style={{ color: COLORS.gray }}>Format</InputLabel>
                <Select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  label="Format"
                  style={{ color: COLORS.dark }}
                >
                  <MenuItem value="json">JSON Data</MenuItem>
                  <MenuItem value="csv">CSV Data</MenuItem>
                  <MenuItem value="pdf">PDF Report</MenuItem>
                  <MenuItem value="excel">Excel Spreadsheet</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth style={{ marginBottom: "24px" }}>
                <InputLabel style={{ color: COLORS.gray }}>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                  style={{ color: COLORS.dark }}
                >
                  <MenuItem value="last7days">Last 7 Days</MenuItem>
                  <MenuItem value="last30days">Last 30 Days</MenuItem>
                  <MenuItem value="last90days">Last 90 Days</MenuItem>
                  <MenuItem value="lastyear">Last Year</MenuItem>
                  <MenuItem value="alltime">All Time</MenuItem>
                </Select>
              </FormControl>
              
              <Button 
                variant="contained" 
                fullWidth
                size="large"
                startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={isExporting}
                style={{
                  background: COLORS.primary,
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '14px',
                  textTransform: 'none',
                  fontSize: '16px'
                }}
              >
                {isExporting ? 'Exporting...' : 'Export Report'}
              </Button>
            </Paper>
          </Grid>
          
          {/* Report Information */}
          <Grid item xs={12} md={6}>
            <Paper style={{ padding: "24px", ...frostedGlassStyle }}>
              <Typography variant="subtitle1" gutterBottom style={{ color: COLORS.dark, fontWeight: 600, marginBottom: '20px' }}>
                Report Contents
              </Typography>
              
              <List>
                <ListItem style={{ padding: '12px 0' }}>
                  <ListItemIcon>
                    <AssessmentIcon style={{ color: COLORS.primary }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Performance Metrics"
                    secondary="Impressions, clicks, shares, and CTR data"
                    primaryTypographyProps={{ style: { fontWeight: 600, color: COLORS.dark } }}
                    secondaryTypographyProps={{ style: { color: COLORS.gray } }}
                  />
                </ListItem>
                <Divider style={{ backgroundColor: COLORS.borderColor }} />
                <ListItem style={{ padding: '12px 0' }}>
                  <ListItemIcon>
                    <TimelineIcon style={{ color: COLORS.secondary }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Temporal Trends"
                    secondary="Historical performance over time"
                    primaryTypographyProps={{ style: { fontWeight: 600, color: COLORS.dark } }}
                    secondaryTypographyProps={{ style: { color: COLORS.gray } }}
                  />
                </ListItem>
                <Divider style={{ backgroundColor: COLORS.borderColor }} />
                <ListItem style={{ padding: '12px 0' }}>
                  <ListItemIcon>
                    <PublicIcon style={{ color: COLORS.success }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Business Impact"
                    secondary="ROI, backlinks, and revenue attribution"
                    primaryTypographyProps={{ style: { fontWeight: 600, color: COLORS.dark } }}
                    secondaryTypographyProps={{ style: { color: COLORS.gray } }}
                  />
                </ListItem>
                <Divider style={{ backgroundColor: COLORS.borderColor }} />
                <ListItem style={{ padding: '12px 0' }}>
                  <ListItemIcon>
                    <BarChartIcon style={{ color: COLORS.tertiary }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Competitive Analysis"
                    secondary="Industry benchmarks and comparisons"
                    primaryTypographyProps={{ style: { fontWeight: 600, color: COLORS.dark } }}
                    secondaryTypographyProps={{ style: { color: COLORS.gray } }}
                  />
                </ListItem>
              </List>
              
              <Alert severity="info" style={{ marginTop: '16px' }}>
                Reports include all data from your selected date range with visualizations and insights.
              </Alert>
            </Paper>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity} 
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

// Main Analytics Dashboard Component
export const AnalyticsDashboard: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const COLORS = getColors(isDarkMode);
  
  const [activeTab, setActiveTab] = useState('overview');
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    overview: {
      totalPRs: 0,
      published: 0,
      activeCampaigns: 0,
      pendingPublications: 0
    },
    performance: {
      totalImpressions: 0,
      totalClicks: 0,
      totalShares: 0,
      averageCTR: 0,
      averageTimeOnPage: 0,
      bounceRate: 0
    },
    business: {
      websiteTrafficIncrease: 0,
      earnedBacklinks: 0,
      conversionRate: 0,
      leadGeneration: 0,
      salesAttribution: 0
    },
    media: {
      earnedMediaValue: 0,
      shareOfVoice: 0,
      sentimentScore: 0,
      positiveCoverage: 0,
      neutralCoverage: 0,
      negativeCoverage: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignStats, setCampaignStats] = useState<{
    totalCampaigns: number;
    activeCampaigns: number;
    totalSpent: number;
    totalBudget: number;
    totalImpressions: number;
  } | null>(null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <AssessmentIcon /> },
    { id: 'temporal', label: 'Temporal Analysis', icon: <TimelineIcon /> },
    { id: 'competitive', label: 'Competitive Intelligence', icon: <BarChartIcon /> },
    { id: 'export', label: 'Export & Reporting', icon: <DownloadIcon /> }
  ];

  const fetchCampaignStats = async () => {
    try {
      const stats = await getCampaignStats();
      if (stats) {
        setCampaignStats(stats);
      }
    } catch (err) {
      console.error('Error fetching campaign stats:', err);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchCampaignStats();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      // Fetch GA4 integration metrics first
      let ga4Metrics: any = null;
      try {
        const integrations = await getIntegrations();
        const ga4Integration = integrations.find((int: any) => int.name === 'Google Analytics' && int.status === 'connected');
        
        if (ga4Integration) {
          console.log('📊 Found GA4 integration, fetching metrics...');
          
          // Try to get latest metrics
          try {
            const metricsData = await getIntegrationMetrics(ga4Integration._id);
            ga4Metrics = metricsData.latestMetrics;
            console.log('✅ GA4 metrics from integration:', ga4Metrics);
            
            // If no metrics or metrics are empty, trigger a sync
            if (!ga4Metrics || Object.keys(ga4Metrics).length === 0) {
              console.log('🔄 No GA4 metrics found, triggering sync...');
              try {
                const syncResult = await syncIntegration(ga4Integration._id);
                if (syncResult.metrics) {
                  ga4Metrics = syncResult.metrics;
                  console.log('✅ GA4 metrics from sync:', ga4Metrics);
                }
              } catch (syncError: any) {
                console.warn('⚠️  GA4 sync failed:', syncError.message);
              }
            }
          } catch (metricsError: any) {
            console.warn('⚠️  Could not fetch GA4 metrics:', metricsError.message);
          }
        }
      } catch (integrationError: any) {
        console.warn('⚠️  Could not fetch integrations:', integrationError.message);
        // Continue without GA4 metrics
      }

      // Try the analytics dashboard endpoint (correct path)
      const response = await fetch(`${API_BASE_URL}/api/pr/analytics/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // If the endpoint doesn't exist, fetch press releases directly (via API client)
        const pressReleases: PressReleaseResponse[] = await getPressReleases();
        
        // Calculate metrics from press releases
        const totalPRs = pressReleases.length;
        const publishedPRs = pressReleases.filter((pr: any) => pr.status === 'published').length;
        const activeCampaigns = pressReleases.filter((pr: any) => ['distributed', 'pending'].includes(pr.status)).length;
        const pendingPublications = pressReleases.filter((pr: any) => pr.status === 'distributed').length;
        
        const totalImpressions = pressReleases.reduce((sum: number, pr: any) => sum + (pr.analytics?.totalImpressions || 0), 0);
        const totalClicks = pressReleases.reduce((sum: number, pr: any) => sum + (pr.analytics?.totalClicks || 0), 0);
        const totalShares = pressReleases.reduce((sum: number, pr: any) => sum + (pr.analytics?.totalShares || 0), 0);
        const averageCTR = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
        
        // Calculate Earned Media Value (EMV): (impressions / 1000) * averageCPM
        const averageCPM = 5;
        const earnedMediaValue = (totalImpressions / 1000) * averageCPM;
        
        // Calculate Share of Voice: (totalImpressions / 100000) * 100, capped at 100%
        const shareOfVoice = Math.min(100, (totalImpressions / 100000) * 100);
        
        // Calculate Sentiment Score based on engagement metrics
        let sentimentScore = 50; // Base neutral score
        if (totalImpressions > 0) {
          const clickRate = (totalClicks / totalImpressions) * 100;
          const shareRate = (totalShares / totalImpressions) * 100;
          const engagementBoost = Math.min(30, (clickRate * 0.5) + (shareRate * 2));
          sentimentScore = Math.min(100, Math.max(0, 50 + engagementBoost));
        }
        
        // Calculate sentiment distribution
        let positiveCoverage = 0;
        let neutralCoverage = 0;
        let negativeCoverage = 0;
        
        if (sentimentScore >= 70) {
          positiveCoverage = Math.min(100, sentimentScore + 10);
          neutralCoverage = 100 - positiveCoverage;
        } else if (sentimentScore >= 50) {
          positiveCoverage = sentimentScore - 20;
          neutralCoverage = 100 - positiveCoverage;
        } else if (sentimentScore >= 30) {
          neutralCoverage = sentimentScore + 20;
          positiveCoverage = 100 - neutralCoverage;
        } else {
          negativeCoverage = 100 - sentimentScore;
          neutralCoverage = sentimentScore;
        }
        
        // Ensure percentages add up to 100
        const total = positiveCoverage + neutralCoverage + negativeCoverage;
        if (total > 0) {
          positiveCoverage = Math.round((positiveCoverage / total) * 100);
          neutralCoverage = Math.round((neutralCoverage / total) * 100);
          negativeCoverage = 100 - positiveCoverage - neutralCoverage;
        }
        
        const estimatedBacklinks = Math.floor(publishedPRs * 7);
        const estimatedRevenue = totalClicks * 3.5;
        
        setMetrics({
          overview: {
            totalPRs,
            published: publishedPRs,
            activeCampaigns,
            pendingPublications
          },
          performance: {
            totalImpressions,
            totalClicks,
            totalShares,
            totalBacklinks: 0, // Will be calculated from crawl results when available
            totalSessions: 0, // Will be calculated from GA4 when available
            totalConversions: 0, // Will be calculated from GA4 when available
            averageCTR: parseFloat(averageCTR.toFixed(2)),
            averageTimeOnPage: 2.5,
            bounceRate: 35
          },
          business: {
            websiteTrafficIncrease: publishedPRs * 5,
            earnedBacklinks: estimatedBacklinks,
            conversionRate: 2.8,
            leadGeneration: Math.floor(totalClicks / 10),
            salesAttribution: estimatedRevenue
          },
          media: {
            earnedMediaValue: earnedMediaValue,
            shareOfVoice: shareOfVoice,
            sentimentScore: Math.round(sentimentScore),
            positiveCoverage: positiveCoverage,
            neutralCoverage: neutralCoverage,
            negativeCoverage: negativeCoverage
          }
        });
      } else {
        const data = await response.json();
        
        if (data.success && data.dashboard) {
          const dashboard = data.dashboard;
          const performance = dashboard.performance || {};
          const overview = dashboard.overview || {};
          
          // Use real data from API
          let totalImpressions = performance.totalImpressions || 0;
          let totalClicks = performance.totalClicks || 0;
          let totalShares = performance.totalShares || 0;
          let totalBacklinks = performance.totalBacklinks || 0;
          let totalSessions = performance.totalSessions || 0;
          let totalConversions = performance.totalConversions || 0;
          let averageCTR = performance.averageCTR || (totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0);
          let averageTimeOnPage = performance.averageTimeOnPage || 0;
          let bounceRate = performance.bounceRate || 35;

          // Merge GA4 integration metrics if available (prioritize real GA4 data)
          if (ga4Metrics) {
            console.log('📊 Merging GA4 integration metrics:', ga4Metrics);
            
            // Use GA4 sessions (more accurate than estimated)
            if (ga4Metrics.sessions !== undefined && ga4Metrics.sessions > 0) {
              totalSessions = ga4Metrics.sessions;
            }
            
            // Use GA4 page views (can be used for impressions if higher)
            if (ga4Metrics.pageViews !== undefined && ga4Metrics.pageViews > 0) {
              // Use pageViews as impressions if it's higher than current impressions
              if (ga4Metrics.pageViews > totalImpressions) {
                totalImpressions = ga4Metrics.pageViews;
              }
            }
            
            // Use GA4 conversions (real conversion data)
            if (ga4Metrics.conversions !== undefined && ga4Metrics.conversions > 0) {
              totalConversions = ga4Metrics.conversions;
            }
            
            // Use GA4 bounce rate (real bounce rate)
            if (ga4Metrics.bounceRate !== undefined && ga4Metrics.bounceRate > 0) {
              bounceRate = ga4Metrics.bounceRate;
            }
            
            // Use GA4 average session duration (convert to seconds if needed)
            if (ga4Metrics.avgSessionDuration !== undefined && ga4Metrics.avgSessionDuration > 0) {
              // GA4 returns duration in seconds
              averageTimeOnPage = ga4Metrics.avgSessionDuration;
            }
            
            // Recalculate CTR with real GA4 data
            if (ga4Metrics.pageViews > 0 && totalClicks > 0) {
              averageCTR = (totalClicks / ga4Metrics.pageViews) * 100;
            }
            
            console.log('✅ Updated metrics with GA4 data:', {
              totalSessions,
              totalConversions,
              totalImpressions,
              bounceRate,
              averageTimeOnPage,
              averageCTR
            });
          }

          // Merge GA4 integration metrics if available (prioritize real GA4 data)
          if (ga4Metrics) {
            console.log('📊 Merging GA4 integration metrics:', ga4Metrics);
            
            // Use GA4 sessions (more accurate than estimated)
            if (ga4Metrics.sessions !== undefined && ga4Metrics.sessions > 0) {
              totalSessions = ga4Metrics.sessions;
            }
            
            // Use GA4 users
            const ga4Users = ga4Metrics.users || 0;
            
            // Use GA4 page views (can be used for impressions if higher)
            if (ga4Metrics.pageViews !== undefined && ga4Metrics.pageViews > 0) {
              // Use pageViews as impressions if it's higher than current impressions
              if (ga4Metrics.pageViews > totalImpressions) {
                totalImpressions = ga4Metrics.pageViews;
              }
            }
            
            // Use GA4 conversions (real conversion data)
            if (ga4Metrics.conversions !== undefined && ga4Metrics.conversions > 0) {
              totalConversions = ga4Metrics.conversions;
            }
            
            // Use GA4 bounce rate (real bounce rate)
            if (ga4Metrics.bounceRate !== undefined && ga4Metrics.bounceRate > 0) {
              bounceRate = ga4Metrics.bounceRate;
            }
            
            // Use GA4 average session duration (convert to seconds if needed)
            if (ga4Metrics.avgSessionDuration !== undefined && ga4Metrics.avgSessionDuration > 0) {
              // GA4 returns duration in seconds
              averageTimeOnPage = ga4Metrics.avgSessionDuration;
            }
            
            // Recalculate CTR with real GA4 data
            if (ga4Metrics.pageViews > 0 && totalClicks > 0) {
              averageCTR = (totalClicks / ga4Metrics.pageViews) * 100;
            }
            
            console.log('✅ Updated metrics with GA4 data:', {
              totalSessions,
              totalConversions,
              totalImpressions,
              bounceRate,
              averageTimeOnPage,
              averageCTR
            });
          }
          
          // Debug logging
          console.log('📊 Dashboard API Response:', {
            performance: {
              totalSessions,
              totalBacklinks,
              totalConversions,
              totalImpressions,
              totalClicks,
              totalShares,
              averageTimeOnPage,
              bounceRate
            },
            overview
          });
          
          // Calculate business metrics from real data
          const publishedCount = overview.publishedPRs || overview.published || 0;
          
          // Calculate Earned Media Value (EMV): (impressions / 1000) * averageCPM
          // Industry standard CPM is $5 per 1000 impressions
          const averageCPM = 5;
          const earnedMediaValue = (totalImpressions / 1000) * averageCPM;
          
          // Calculate Share of Voice: (totalImpressions / 100000) * 100, capped at 100%
          const shareOfVoice = Math.min(100, (totalImpressions / 100000) * 100);
          
          // Calculate Sentiment Score based on engagement metrics
          // Higher engagement (clicks, shares, low bounce) = positive sentiment
          // Formula: Base score (50) + engagement boost - negative signals
          let sentimentScore = 50; // Base neutral score
          
          if (totalImpressions > 0) {
            // Positive signals
            const clickRate = (totalClicks / totalImpressions) * 100;
            const shareRate = (totalShares / totalImpressions) * 100;
            const engagementBoost = Math.min(30, (clickRate * 0.5) + (shareRate * 2));
            
            // Negative signals (if bounce rate is high, reduce sentiment)
            // Use real bounce rate from GA4 (already set above)
            const negativePenalty = Math.max(0, (bounceRate - 30) * 0.2);
            
            sentimentScore = Math.min(100, Math.max(0, 50 + engagementBoost - negativePenalty));
          }
          
          // Calculate sentiment distribution based on score
          // If score is high (70+), mostly positive; if low (30-), mostly negative
          let positiveCoverage = 0;
          let neutralCoverage = 0;
          let negativeCoverage = 0;
          
          if (sentimentScore >= 70) {
            positiveCoverage = Math.min(100, sentimentScore + 10);
            neutralCoverage = 100 - positiveCoverage;
            negativeCoverage = 0;
          } else if (sentimentScore >= 50) {
            positiveCoverage = sentimentScore - 20;
            neutralCoverage = 100 - positiveCoverage;
            negativeCoverage = 0;
          } else if (sentimentScore >= 30) {
            neutralCoverage = sentimentScore + 20;
            positiveCoverage = 100 - neutralCoverage;
            negativeCoverage = 0;
          } else {
            negativeCoverage = 100 - sentimentScore;
            neutralCoverage = sentimentScore;
            positiveCoverage = 0;
          }
          
          // Ensure percentages add up to 100
          const total = positiveCoverage + neutralCoverage + negativeCoverage;
          if (total > 0) {
            positiveCoverage = Math.round((positiveCoverage / total) * 100);
            neutralCoverage = Math.round((neutralCoverage / total) * 100);
            negativeCoverage = 100 - positiveCoverage - neutralCoverage;
          }
          
          const estimatedRevenue = totalClicks * 3.5;
          
          // Sort published articles by date (latest first) and limit to 5
          const publishedArticles = (dashboard.publishedArticles || [])
            .sort((a: any, b: any) => {
              const dateA = new Date(a.publishedAt || 0).getTime();
              const dateB = new Date(b.publishedAt || 0).getTime();
              return dateB - dateA;
            });
          
          setMetrics({
            overview: {
              totalPRs: overview.totalPRs || 0,
              published: publishedCount,
              activeCampaigns: overview.activeCampaigns || 0,
              pendingPublications: overview.pendingPublications || 0,
              publishedArticlesCount: overview.publishedArticlesCount || publishedArticles.length
            },
            performance: {
              totalImpressions,
              totalClicks,
              totalShares,
              totalBacklinks,
              totalSessions,
              totalConversions,
              averageCTR: parseFloat(averageCTR.toFixed(2)),
              averageTimeOnPage: averageTimeOnPage, // From GA4 avgSessionDuration (in seconds)
              bounceRate: bounceRate // From GA4 bounce rate
            },
            business: {
              // Use real GA4 sessions for website traffic increase
              websiteTrafficIncrease: totalSessions > 0 ? totalSessions : Math.round(totalImpressions * 0.05),
              earnedBacklinks: totalBacklinks,
              conversionRate: totalSessions > 0 ? ((totalConversions / totalSessions) * 100) : 0, // Real GA4 conversion rate
              leadGeneration: totalSessions > 0 ? Math.floor(totalSessions * 0.05) : Math.floor(totalClicks / 10), // Based on real sessions
              salesAttribution: totalConversions > 0 ? (totalConversions * 3.5) : estimatedRevenue // Based on real conversions
            },
            media: {
              earnedMediaValue: earnedMediaValue,
              shareOfVoice: shareOfVoice,
              sentimentScore: Math.round(sentimentScore),
              positiveCoverage: positiveCoverage,
              neutralCoverage: neutralCoverage,
              negativeCoverage: negativeCoverage
            },
            publishedArticles: publishedArticles
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      // Don't show error to user, just use default metrics silently
      setMetrics({
        overview: { totalPRs: 0, published: 0, activeCampaigns: 0, pendingPublications: 0 },
        performance: { totalImpressions: 0, totalClicks: 0, totalShares: 0, averageCTR: 0, averageTimeOnPage: 0, bounceRate: 0 },
        business: { websiteTrafficIncrease: 0, earnedBacklinks: 0, conversionRate: 0, leadGeneration: 0, salesAttribution: 0 },
        media: { earnedMediaValue: 0, shareOfVoice: 0, sentimentScore: 0, positiveCoverage: 0, neutralCoverage: 0, negativeCoverage: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" style={{ 
      padding: '24px', 
      background: theme.palette.background.default,
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Typography 
          variant="h4" 
          style={{ 
            fontWeight: 'bold', 
            color: COLORS.primary, 
            marginBottom: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px' 
          }}
        >
          <AssessmentIcon style={{ fontSize: '32px' }} />
          Analytics Dashboard
        </Typography>
        <Typography variant="body1" style={{ color: COLORS.gray }}>
          Track and analyze your PR campaign performance and ROI
        </Typography>
      </div>

      {/* Info Message */}
      <Alert 
        severity="info" 
        style={{ 
          marginBottom: '24px',
          borderRadius: '8px',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'}`
        }}
        icon={<InfoIcon />}
      >
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          Analytical data is reflected with 24-48 hrs
        </Typography>
      </Alert>
      
      {/* Tab Navigation */}
      <div style={{ marginBottom: "24px", display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '12px' }}>
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'contained' : 'outlined'}
            startIcon={tab.icon}
            onClick={() => setActiveTab(tab.id)}
            style={{
              backgroundColor: activeTab === tab.id ? COLORS.primary : 'transparent',
              color: activeTab === tab.id ? 'white' : COLORS.primary,
              border: activeTab === tab.id ? 'none' : `2px solid ${COLORS.primary}`,
              borderRadius: '12px',
              fontWeight: '600',
              padding: '10px 24px',
              textTransform: 'none',
              fontSize: '16px'
            }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <CoreMetricsDisplay metrics={metrics} loading={loading} onRefresh={fetchDashboardData} campaignStats={campaignStats} />}
      {activeTab === 'temporal' && <TemporalAnalysis />}
      {activeTab === 'competitive' && <CompetitiveIntelligence />}
      {activeTab === 'export' && <ExportReporting />}
    </Container>
  );
};
